"use client";

import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { fetchPageContentThunk } from "@/store/globalSlice";
import { useErrorToast } from "./useErrorToast";
import { useSearchParams } from "next/navigation";
import { normalizeLocale } from "@/utils/localeUtil";
import { logClientErrorWithContext } from "@/lib/logger/src/logClientErrorWithContext";

// track in-flight requests to avoid duplicate XHRs for the same slug+locale
const inFlightRequests = new Set<string>();

// Export for testing purposes - allows resetting the in-flight set
export function __resetInFlight() {
  inFlightRequests.clear();
}

// This hook fetches and returns the content of a page based on its slug.
export function usePageContent(slug: string) {
  const dispatch = useDispatch<AppDispatch>();
  const handleClientApiError = useErrorToast()
  const searchParams = useSearchParams();
  const languageCode = normalizeLocale(searchParams?.get("locale"));
  const { pageContents, status } = useSelector(
    (state: RootState) => state.global
  );

  const cacheKey = `${slug}::${languageCode}`;
  const pageContent = pageContents[cacheKey] ?? null;
  const fetchedKeyRef = useRef<string | null>(null);

  // Fetch when we don't have content for the active language. To avoid
  // duplicate requests we keep a module-level set of in-flight slug+locale
  // keys and skip dispatch if a request is already pending.
  useEffect(() => {
    const key = cacheKey;

    // If we already have content for this slug+locale, nothing to do.
    if (pageContent) {
      fetchedKeyRef.current = key;
      return;
    }

    // If a fetch for this slug+locale is already in-flight, don't start another.
    if (inFlightRequests.has(key)) return;

    // mark in-flight and dispatch
    inFlightRequests.add(key);
    dispatch(fetchPageContentThunk({ languageCode, slug }))
      .unwrap()
      .catch((err) => {
        logClientErrorWithContext({
          alertName: "QRV_CORP_ERROR_STRAPI_002",
          error: err,
          pageName: "usePageContent",
          apiName: `/api/strapi/load?slug=${slug}&locale=${languageCode}`,
          httpMethod: "GET",
          correlationId: "",
          operation: "",
          operationSignature: "",
        });
        handleClientApiError()
      })
      .finally(() => {
        inFlightRequests.delete(key);
      });
  }, [dispatch, languageCode, slug]);
  return { pageContent, status };
}
